import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from 'mdi-react/CloseIcon';
import EyeOffOutlineIcon from 'mdi-react/EyeOffOutlineIcon';
import EyeOutlineIcon from 'mdi-react/EyeOutlineIcon';
import PlusIcon from 'mdi-react/PlusIcon';
import TrashCanOutlineIcon from 'mdi-react/TrashCanOutlineIcon';
import PropTypes, { bool, func, object, string } from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import SECRET_TYPE_SCHEMAS, { SECRET_TYPES } from 'services/secrets/secretTypeSchemas';
import { I18n } from 'utils/i18n';

const fieldGroupSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.5,
};

const labelSx = {
  fontSize: theme => theme.typography.pxToRem(14),
  fontWeight: 500,
  color: 'text.primary',
  lineHeight: 1.3,
};

const helperSx = {
  fontSize: theme => theme.typography.pxToRem(12),
  color: 'text.secondary',
  lineHeight: 1.4,
  mb: 0.25,
};

const isFieldActive = (field, value) => {
  if (!field.when) return true;
  const [refKey, expected] = Object.entries(field.when)[0];
  return value?.[refKey] === expected;
};

const getActiveFields = (type, value) => {
  const schema = SECRET_TYPE_SCHEMAS[type];
  if (!schema) return [];
  return schema.fields.filter(field => isFieldActive(field, value));
};

const buildEmptyValue = type => {
  const schema = SECRET_TYPE_SCHEMAS[type];
  if (!schema) return {};
  const value = {};
  schema.fields.forEach(field => {
    if (field.input === 'kv_list') {
      value[field.key] = [{ name: '', value: '' }];
    } else if (field.input === 'select') {
      value[field.key] = field.default ?? field.options?.[0] ?? '';
    } else {
      value[field.key] = '';
    }
  });
  return value;
};

const isValueValid = (type, value) => {
  const activeFields = getActiveFields(type, value);
  return activeFields.every(field => {
    if (!field.required) return true;
    if (field.input === 'kv_list') {
      const list = Array.isArray(value[field.key]) ? value[field.key] : [];
      return list.length > 0 && list.every(entry => entry.name?.trim() && entry.value?.trim());
    }
    return value[field.key]?.toString().trim();
  });
};

const sanitizeValue = (type, value) => {
  const activeFields = getActiveFields(type, value);
  const result = {};
  activeFields.forEach(field => {
    if (field.input === 'kv_list') {
      const list = Array.isArray(value[field.key]) ? value[field.key] : [];
      result[field.key] = list
        .filter(entry => entry.name?.trim())
        .map(entry => ({ name: entry.name.trim(), value: entry.value ?? '' }));
    } else if (field.required || value[field.key]?.toString().trim()) {
      result[field.key] = value[field.key] ?? '';
    }
  });
  return result;
};

const FieldLabel = ({ label, required = false, helper = null }) => (
  <>
    <Typography component="span" sx={labelSx}>
      {required && (
        <Box component="span" sx={{ color: 'error.main', mr: '2px' }}>
          *
        </Box>
      )}
      {label}
    </Typography>
    {helper && <Typography sx={helperSx}>{helper}</Typography>}
  </>
);

FieldLabel.propTypes = {
  label: string.isRequired,
  required: bool,
  helper: string,
};

const PasswordTextField = ({
  value = '',
  onChange,
  placeholder = '',
  required = false,
  autoFocus = false,
}) => {
  const [show, setShow] = useState(false);
  return (
    <TextField
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      size="small"
      fullWidth
      required={required}
      autoFocus={autoFocus}
      type={show ? 'text' : 'password'}
      placeholder={placeholder}
      slotProps={{
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                size="small"
                edge="end"
                onClick={() => setShow(prev => !prev)}
                aria-label="toggle visibility"
              >
                {show ? <EyeOffOutlineIcon size={16} /> : <EyeOutlineIcon size={16} />}
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );
};

PasswordTextField.propTypes = {
  value: string,
  onChange: func.isRequired,
  placeholder: string,
  required: bool,
  autoFocus: bool,
};

const SecretField = ({ field, value = '', onChange }) => {
  const label = I18n.t(`AgenticChatFlow.SecretField_${field.key}`);
  const placeholder = field.placeholder || '';
  const helper = field.helper || null;

  if (field.input === 'kv_list') {
    const entries = Array.isArray(value) && value.length > 0 ? value : [{ name: '', value: '' }];
    const update = (index, patch) => {
      onChange(entries.map((entry, i) => (i === index ? { ...entry, ...patch } : entry)));
    };
    const add = () => onChange([...entries, { name: '', value: '' }]);
    const remove = index => {
      const next = entries.filter((_, i) => i !== index);
      onChange(next.length === 0 ? [{ name: '', value: '' }] : next);
    };
    return (
      <Box sx={fieldGroupSx}>
        <FieldLabel label={label} required={field.required} helper={helper} />
        {entries.map((entry, index) => (
          <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              size="small"
              fullWidth
              placeholder={I18n.t('AgenticChatFlow.SecretKvName')}
              value={entry.name}
              onChange={e => update(index, { name: e.target.value })}
            />
            <TextField
              size="small"
              fullWidth
              placeholder={I18n.t('AgenticChatFlow.SecretKvValue')}
              value={entry.value}
              onChange={e => update(index, { value: e.target.value })}
            />
            <IconButton
              size="small"
              onClick={() => remove(index)}
              aria-label={I18n.t('Common.Delete')}
              disabled={entries.length === 1 && !entry.name && !entry.value}
            >
              <TrashCanOutlineIcon size={16} />
            </IconButton>
          </Box>
        ))}
        <Button
          variant="text"
          color="secondary"
          size="small"
          startIcon={<PlusIcon size={14} />}
          sx={{ alignSelf: 'flex-start' }}
          onClick={add}
        >
          {I18n.t('AgenticChatFlow.SecretKvAdd')}
        </Button>
      </Box>
    );
  }

  const isTextarea = field.input === 'textarea';
  const isPassword = field.input === 'password';
  const isSelect = field.input === 'select';

  return (
    <Box sx={fieldGroupSx}>
      <FieldLabel label={label} required={field.required} helper={helper} />
      {isPassword && (
        <PasswordTextField
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={field.required}
        />
      )}
      {isSelect && (
        <TextField
          select
          value={value ?? field.default ?? ''}
          onChange={e => onChange(e.target.value)}
          size="small"
          fullWidth
          required={field.required}
        >
          {(field.options || []).map(option => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      )}
      {!isPassword && !isSelect && (
        <TextField
          value={value ?? ''}
          onChange={e => onChange(e.target.value)}
          size="small"
          fullWidth
          required={field.required}
          multiline={isTextarea}
          minRows={isTextarea ? 4 : undefined}
          placeholder={placeholder}
        />
      )}
    </Box>
  );
};

SecretField.propTypes = {
  field: object.isRequired,
  value: PropTypes.any,
  onChange: func.isRequired,
};

const CreateSecretDialog = ({ open, type: initialType = null, onClose, onCreate }) => {
  const [type, setType] = useState(initialType || SECRET_TYPES[0]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [value, setValue] = useState(() => buildEmptyValue(initialType || SECRET_TYPES[0]));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const nextType =
        initialType && SECRET_TYPE_SCHEMAS[initialType] ? initialType : SECRET_TYPES[0];
      setType(nextType);
      setName('');
      setDescription('');
      setValue(buildEmptyValue(nextType));
    }
  }, [open, initialType]);

  const onTypeChange = nextType => {
    setType(nextType);
    setValue(buildEmptyValue(nextType));
  };

  const schema = SECRET_TYPE_SCHEMAS[type];

  const canSave = useMemo(
    () => Boolean(name.trim()) && isValueValid(type, value),
    [name, type, value],
  );

  const handleCreate = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await onCreate({
        name: name.trim(),
        type,
        description,
        value: sanitizeValue(type, value),
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle
        disableTypography
        sx={{
          fontSize: theme => theme.typography.pxToRem(18),
          fontWeight: 700,
          p: 2.5,
          px: 3,
          pb: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography component="span" sx={{ fontSize: 18, fontWeight: 700 }}>
          {I18n.t('AgenticChatFlow.CreateSecret')}
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label={I18n.t('Common.close')}>
          <CloseIcon size={18} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Box sx={fieldGroupSx}>
            <FieldLabel label={I18n.t('AgenticChatFlow.SecretName')} required />
            <TextField
              value={name}
              onChange={e => setName(e.target.value)}
              size="small"
              fullWidth
              required
              autoFocus
              placeholder={I18n.t('AgenticChatFlow.SecretNamePlaceholder')}
            />
          </Box>
          <Box sx={fieldGroupSx}>
            <FieldLabel label={I18n.t('AgenticChatFlow.SecretType')} required />
            <TextField
              select
              value={type}
              onChange={e => onTypeChange(e.target.value)}
              size="small"
              fullWidth
              required
            >
              {SECRET_TYPES.map(t => (
                <MenuItem key={t} value={t}>
                  {I18n.t(`AgenticChatFlow.SecretType_${t}`)}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={fieldGroupSx}>
            <FieldLabel label={I18n.t('AgenticChatFlow.SecretDescription')} />
            <TextField
              value={description}
              onChange={e => setDescription(e.target.value)}
              size="small"
              fullWidth
              multiline
              minRows={2}
              placeholder={I18n.t('AgenticChatFlow.SecretDescriptionPlaceholder')}
            />
          </Box>
          {schema?.fields
            .filter(field => isFieldActive(field, value))
            .map(field => (
              <SecretField
                key={field.key}
                field={field}
                value={value[field.key]}
                onChange={next => setValue(prev => ({ ...prev, [field.key]: next }))}
              />
            ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} color="inherit">
          {I18n.t('AgenticChatFlow.Cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disableElevation
          onClick={handleCreate}
          disabled={saving || !canSave}
        >
          {I18n.t('AgenticChatFlow.Create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CreateSecretDialog.propTypes = {
  open: bool.isRequired,
  type: string,
  onClose: func.isRequired,
  onCreate: func.isRequired,
};

export default CreateSecretDialog;
